import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '@/api/http'
import { serviceDailyQuery, serviceDetailQuery } from '@/api/queries'
import type { Target } from '@/api/types'
import { StatusBadge } from '@/components/StatusBadge'
import { ServiceFormModal } from '@/components/ServiceFormModal'
import { ShareLink } from '@/components/ShareLink'
import { TargetFormModal } from '@/components/TargetFormModal'
import { UptimeRows } from '@/components/UptimeRows'
import { Button, Card, EmptyState, ErrorBox, Loading } from '@/components/ui'
import { useAuth } from '@/lib/useAuth'
import { formatMs, formatPercent, formatRelative, intervalLabel } from '@/lib/format'

/**
 * Detail sluzby je zamerne `index` route, ne layout.
 *
 * Kdyby se soubor jmenoval `_app.services.$serviceId.tsx`, stal by se
 * rodicem cesty `/services/$serviceId/targets/$targetId` — a protoze tahle
 * komponenta nevykresluje <Outlet />, zobrazoval by se detail sluzby i na
 * adrese detailu cile. Jako `index` je to samostatny list stromu.
 */
/** Okno prehledu nad tabulkou. Mesic je kompromis mezi detailem a kontextem. */
const OVERVIEW_DAYS = 30

export const Route = createFileRoute('/_app/services/$serviceId/')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(serviceDetailQuery(params.serviceId)),
  component: ServiceDetailPage,
})

function ServiceDetailPage() {
  // Parametry cesty jsou typovane podle nazvu souboru — preklep v
  // `serviceId` by neprosel prekladem.
  const { serviceId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const { data, isLoading } = useQuery(serviceDetailQuery(serviceId))
  const { data: daily } = useQuery(serviceDailyQuery(serviceId, OVERVIEW_DAYS))
  const [addingTarget, setAddingTarget] = useState(false)
  const [editingService, setEditingService] = useState(false)
  const [editingTarget, setEditingTarget] = useState<Target | null>(null)
  const [error, setError] = useState<string | null>(null)

  const deleteService = useMutation({
    mutationFn: async () => api.delete(`/services/${serviceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      navigate({ to: '/dashboard' })
    },
    onError: (err) => setError(errorMessage(err, 'Smazani se nezdarilo')),
  })

  const deleteTarget = useMutation({
    mutationFn: async (targetId: string) => api.delete(`/targets/${targetId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services', serviceId] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (err) => setError(errorMessage(err, 'Smazani se nezdarilo')),
  })

  if (isLoading || !data) return <Loading />

  const { service, targets } = data
  const isAdmin = user?.role === 'ADMIN'

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-brand-700">
            ← Prehled
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">{service.name}</h1>
          {service.description && <p className="text-sm text-slate-500">{service.description}</p>}
          {service.isPublic && (
            <Link
              to="/status/$publicId"
              params={{ publicId: service.publicId }}
              className="mt-1 inline-block break-all text-sm text-brand-700 hover:underline"
            >
              /status/{service.publicId}
            </Link>
          )}
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setEditingService(true)}>
              Upravit
            </Button>
            <Button onClick={() => setAddingTarget(true)}>Novy cil</Button>
          </div>
        )}
      </div>

      <ErrorBox message={error} />

      {/* Sdileny odkaz spravuje jen admin — MEMBER by ho stejne nemohl zapnout. */}
      {isAdmin && <ShareLink service={service} />}

      {targets.length === 0 ? (
        <EmptyState
          title="Sluzba zatim nema zadny cil"
          hint={isAdmin ? 'Pridej URL, kterou ma monitoring hlidat.' : undefined}
        />
      ) : (
        <>
          {/* Prehled nad tabulkou: na jeden pohled je videt, ktery cil za
              posledni mesic vypadaval a ktery jel bez zaskobrtnuti. */}
          <Card>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="text-sm font-medium text-slate-700">Dostupnost za {OVERVIEW_DAYS} dnu</h2>
              <span className="text-xs text-slate-400">dny bez mereni jsou sede</span>
            </div>
            <UptimeRows serviceId={serviceId} rows={daily ?? []} days={OVERVIEW_DAYS} />
          </Card>

          <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Stav</th>
                <th className="px-4 py-3">Cil</th>
                <th className="px-4 py-3">Interval</th>
                <th className="px-4 py-3 text-right">Odezva</th>
                <th className="px-4 py-3 text-right">24h SLA</th>
                <th className="px-4 py-3">Mereno</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {targets.map((target) => (
                <tr key={target.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <StatusBadge status={target.currentStatus} />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to="/services/$serviceId/targets/$targetId"
                      params={{ serviceId, targetId: target.id }}
                      className="font-medium text-slate-900 hover:text-brand-700"
                    >
                      {target.name}
                    </Link>
                    <p className="max-w-[280px] truncate text-xs text-slate-400">{target.url}</p>
                    {!target.isActive && <span className="text-xs text-amber-600">mereni vypnuto</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {target.method} · {intervalLabel(target.intervalSeconds)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatMs(target.lastResponseTimeMs)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatPercent(target.uptime24h)}</td>
                  <td className="px-4 py-3 text-slate-500">{formatRelative(target.lastCheckedAt)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" onClick={() => setEditingTarget(target)}>
                          Upravit
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => {
                            // Mazani cile bere s sebou i celou historii mereni,
                            // takze se ptame — vratit zpet uz to nejde.
                            if (confirm(`Smazat cil "${target.name}" vcetne namerenych dat?`)) {
                              deleteTarget.mutate(target.id)
                            }
                          }}
                        >
                          Smazat
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </Card>
        </>
      )}

      {isAdmin && (
        <div className="border-t border-slate-200 pt-4">
          <Button
            variant="ghost"
            className="text-red-600 hover:bg-red-50"
            onClick={() => {
              if (confirm(`Smazat sluzbu "${service.name}" vcetne vsech cilu a dat?`)) {
                deleteService.mutate()
              }
            }}
          >
            Smazat sluzbu
          </Button>
        </div>
      )}

      {addingTarget && <TargetFormModal serviceId={serviceId} onClose={() => setAddingTarget(false)} />}
      {editingTarget && (
        <TargetFormModal serviceId={serviceId} target={editingTarget} onClose={() => setEditingTarget(null)} />
      )}
      {editingService && <ServiceFormModal service={service} onClose={() => setEditingService(false)} />}
    </div>
  )
}
